import React, { useCallback } from 'react';
import { OBSERVATION_SPAWN_Y } from '../../../maps/MultiplayerObserveMap';
import { ServiceRegistry } from '../../../core/ServiceRegistry';
import { EventBus, EVENTS } from '../../../../shared/EventBus';
import { CopyButton } from '../CopyButton';

/**
 * TeleportTestBarContent — quick row of teleport buttons for GM testing.
 *
 * Was previously embedded in the HUD's DebugToolbox container with
 * visibility gated by `window.forceDebug || window.playwrightTest`. Since
 * the 2026-05-12 DebugLauncher refactor removed the auto-set of
 * `forceDebug` on /debug, this surface had become dead in normal play —
 * Playwright tests still set forceDebug, so test scenarios are unaffected.
 *
 * Now mountable from the DebugLauncher (registry id `teleport`). The
 * teleport action calls PlayerManager.teleportPlayer directly + emits
 * the server-notify event, matching useDevActions's 'teleport' handler.
 *
 * Visibility is no longer controlled inside this component — it always
 * renders when mounted.
 */

const positions = Array.from({ length: 20 }, (_, i) => i + 1);

function teleport(x: number, y: number): void {
  const registry = ServiceRegistry.getInstance();
  const entitySystem = registry.get('EntitySystem', { optional: true });
  entitySystem?.getPlayerManager()?.teleportPlayer(x, y);
  EventBus.getInstance().emit(EVENTS.PLAYER_ACTION_INTENT, {
    type: 'teleport',
    payload: { x, y },
  });
}

export const TeleportTestBarContent: React.FC = () => {
  const onGo = useCallback((x: number, y: number) => teleport(x, y), []);

  return (
    <div
      data-testid="teleport-bar-panel"
      style={{
        color: '#9ad8ff',
        fontFamily: 'monospace',
        fontSize: 11,
        padding: '4px 6px',
        userSelect: 'none',
      }}
    >
      <div style={{ color: '#cdd6df', fontSize: 10, marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Go to position 1-20 (X increases by 100)</span>
        <CopyButton getText={() => {
          const registry = ServiceRegistry.getInstance();
          const entitySystem = registry.get('EntitySystem', { optional: true });
          const p = entitySystem?.getPlayerManager()?.getPlayer();
          const cur = p ? `(${Math.round(p.position.x)}, ${Math.round(p.position.y)})` : 'n/a';
          return [
            `TELEPORT BAR @ ${new Date().toISOString()}`,
            `current pos: ${cur}`,
            `spawn y:     ${OBSERVATION_SPAWN_Y}`,
            `positions:   ${positions.map(i => `pos${i}=(${i * 100}, ${OBSERVATION_SPAWN_Y})`).join(', ')}`,
          ].join('\n');
        }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 4 }}>
        {positions.map((positionIndex) => {
          const targetX = positionIndex * 100;
          return (
            <button
              key={positionIndex}
              type="button"
              data-testid={`teleport-pos-${positionIndex}`}
              title={`Go to position ${positionIndex} (${targetX}, ${OBSERVATION_SPAWN_Y})`}
              onClick={() => onGo(targetX, OBSERVATION_SPAWN_Y)}
              style={{
                background: 'rgba(30, 144, 255, 0.14)',
                border: '1px solid rgba(30, 144, 255, 0.55)',
                borderRadius: 4,
                color: '#d9f0ff',
                fontSize: 10,
                lineHeight: 1.2,
                padding: '6px 4px',
                cursor: 'pointer',
              }}
            >
              {`Go ${positionIndex}`}
            </button>
          );
        })}
      </div>
    </div>
  );
};
